import axios from '../../utils/axiosConfig';

const encodePcapPath = (filename) =>
	String(filename || '').split('/').map(encodeURIComponent).join('/');

/**
 * PCAP Viewer Redux Actions
 * Handles fetching pcap list and downloading pcap files
 */

/**
 * Get list of available pcap files with metadata
 */
export const getPcapList = () => async (dispatch) => {
	try {
		const config = {
			headers: {
				'Content-Type': 'application/json'
			}
		};

		const { data } = await axios.get(`/api/pcap/list`, config);

		if (data && data.status) {
			return data;
		} else {
			return { status: false, msg: data?.msg || 'Failed to fetch pcap list' };
		}
	} catch (err) {
		console.error('Error fetching pcap list:', err);
		return { status: false, msg: 'Error fetching pcap list' };
	}
};

/**
 * Download a specific pcap file by filename
 * This opens the download in a new window/tab
 */
export const downloadPcap = (filename) => {
	// Open download URL in new window
	const url = `/api/pcap/download/${encodePcapPath(filename)}`;
	window.open(url, '_blank');
};

/**
 * Analyze a pcap file and return packet list with pagination
 * @param {string} filename - The pcap filename to analyze
 * @param {number} page - Page number (1-indexed)
 * @param {number} pageSize - Number of packets per page (default: 100)
 */
export const analyzePcap = (filename, page = 1, pageSize = 100) => async (dispatch) => {
	try {
		const config = {
			headers: {
				'Content-Type': 'application/json'
			},
			params: {
				filename,
				page,
				page_size: pageSize
			}
		};

		const { data } = await axios.get(`/api/pcap/analyze`, config);

		if (data && data.status) {
			return data;
		} else {
			return { status: false, msg: data?.msg || 'Failed to analyze pcap file' };
		}
	} catch (err) {
		console.error('Error analyzing pcap file:', err);
		return { status: false, msg: 'Error analyzing pcap file' };
	}
};

/**
 * Get detailed information for a specific packet
 * @param {string} filename - The pcap filename
 * @param {number} packetIndex - The index of the packet (0-indexed)
 */
export const getPacketDetail = (filename, packetIndex) => async (dispatch) => {
	try {
		const config = {
			headers: {
				'Content-Type': 'application/json'
			},
			params: {
				filename,
				index: packetIndex
			}
		};

		const { data } = await axios.get(`/api/pcap/packet-detail`, config);

		if (data && data.status) {
			return data;
		} else {
			return { status: false, msg: data?.msg || 'Failed to fetch packet details' };
		}
	} catch (err) {
		console.error('Error fetching packet details:', err);
		return { status: false, msg: 'Error fetching packet details' };
	}
};


/**
 * Request a packet capture on a watcher. The C2C enqueues a command the
 * appliance picks up; the capture auto-stops at the 30-minute cutoff.
 */
export const startPcap = (watcherId, filter = '') => async () => {
	try {
		const { data } = await axios.post('/api/pcap/start', { watcherId, filter });
		return data || { status: false, msg: 'No response' };
	} catch (err) {
		return { status: false, msg: err?.response?.data?.msg || err?.message || 'Failed to start capture' };
	}
};

/** Stop the running capture on a watcher. */
export const stopPcap = (watcherId) => async () => {
	try {
		const { data } = await axios.post('/api/pcap/stop', { watcherId });
		return data || { status: false, msg: 'No response' };
	} catch (err) {
		return { status: false, msg: err?.response?.data?.msg || err?.message || 'Failed to stop capture' };
	}
};
